import { readPreload, buildPreloadKey } from "@/api/preload";

/**
 * 首页数据区块组件（HeroBanner、AdvantagesSection、CtaSection、ContactForm、
 * SolutionsSection、CasesSection、CertificationsSection 等）共用的内嵌预载读取
 * 入口，各自从中挑选自己需要的字段作为 `useState` 初值（需求 11.13：首帧输出
 * 与预渲染 HTML 完全一致）。
 *
 * 首页组件求值阶段会同步触发一次 `bootstrapHome()`（见 `src/pages/home/page.tsx`
 * 的 `ensureBootstrap()`），实际发出的网络请求是 `GET /api/front/home/bootstrap`
 * （聚合接口），而不是各子组件历史上单独调用的 `/api/front/home`、
 * `/api/front/certifications` 等子接口——`bootstrapHome()` 用 `primeInFlight()`
 * 把各子缓存键指向「从聚合结果提取对应字段」的派生 Promise，子组件的
 * `cachedRequest()` 调用命中的是内存缓存，不会再发出独立的网络请求。预渲染
 * 序列化端（`scripts/prerender/serialize.ts`）按「本页实际发出的请求」捕获
 * `/api/front/**` 响应，因此 `__GEO_STATE__.direct` 桶里存在的键只有聚合接口
 * 这一个键，各子组件必须从这个聚合值里取字段，而不是各自的子接口键。
 *
 * 各字段的提取规则与 `homeApi.ts` 的 `bootstrapHome()` 逐一对应：
 * - `home` → `GET /api/front/home` 的响应形状
 * - `solutions` → `getSolutionTree()` 的响应形状（`SolutionCategory[]`）
 * - `cases`（前 3 条） → `getCases({ pageNum: 1, pageSize: 3 })` 的 `rows`
 * - `certifications` → `getCertifications()` 的响应形状
 * - `certificationStats` → `getCertificationStats()` 的响应形状
 */
const HOME_BOOTSTRAP_PRELOAD_KEY = buildPreloadKey("GET", "/api/front/home/bootstrap");

function readHomeBootstrapPreload(): any | undefined {
  return readPreload<any>(HOME_BOOTSTRAP_PRELOAD_KEY);
}

/** 读取首页聚合接口内嵌的 `home` 子字段；无内嵌状态或未命中时返回 `undefined`。 */
export function readHomePreload(): any | undefined {
  return readHomeBootstrapPreload()?.home;
}

/** 读取首页聚合接口内嵌的 `solutions` 子字段（解决方案树）；未命中时返回 `undefined`。 */
export function readHomeSolutionsPreload(): any[] | undefined {
  return readHomeBootstrapPreload()?.solutions;
}

/** 读取首页聚合接口内嵌的 `cases` 子字段（前 3 条已发布案例）；未命中时返回 `undefined`。 */
export function readHomeCasesPreload(): any[] | undefined {
  return readHomeBootstrapPreload()?.cases;
}

/** 读取首页聚合接口内嵌的 `certifications` 子字段；未命中时返回 `undefined`。 */
export function readHomeCertificationsPreload(): any[] | undefined {
  return readHomeBootstrapPreload()?.certifications;
}

/** 读取首页聚合接口内嵌的 `certificationStats` 子字段；未命中时返回 `undefined`。 */
export function readHomeCertificationStatsPreload(): any[] | undefined {
  return readHomeBootstrapPreload()?.certificationStats;
}
